// ** React Imports
import React, { useCallback, useEffect, useState } from 'react'

// ** Util Imports
import { DataFilterConfigType, FilterDataType } from 'src/lib/types/filters'
import { useLocale } from 'src/lib/hooks/useLocal'
import { produce } from 'immer'

// ** Child Imports
import FloGenericDialog from '../feedback-and-dialog/GenericDialog'
import FilterForm from './FilterForm'
import { PlusCircle } from 'react-feather'
import { Button } from '@mui/material'

interface FilterFormDialogProps {
  isQueryBasedFilter: boolean
  primaryButton: string
  secondaryButton: string
  isDialogOpen: boolean
  setIsDialogOpen: any
  title: string
  filterConfig: DataFilterConfigType
  initialFilterData: FilterDataType[]
  filterArrayData: FilterDataType[] | undefined
  setFilterArrayData: any
  startDate: string
  endDate: string
  handleApplyFilters: any
}

const FilterFormDialog: React.FC<FilterFormDialogProps> = ({
  isQueryBasedFilter,
  isDialogOpen,
  setIsDialogOpen,
  title,
  primaryButton,
  secondaryButton,
  filterArrayData,
  setFilterArrayData,
  startDate,
  endDate,
  handleApplyFilters,
  filterConfig,
  initialFilterData
}) => {
  const handleAddFilter = () => {
    if (filterArrayData && filterConfig.columns && filterArrayData.length < filterConfig.columns.length) {
      const modifiedInitialFilterData = { ...initialFilterData[0] }
      const firstAvailableFilterOption = filterConfig.columns?.find(
        option => !filterArrayData?.some(data => data?.columnName === option?.value)
      )
      modifiedInitialFilterData.columnName = firstAvailableFilterOption?.value ?? ''
      setFilterArrayData(
        produce((draft: any) => {
          draft.push(modifiedInitialFilterData)
        })
      )
    }
  }

  const renderFilterForm = useCallback(
    (index: number, filterData: FilterDataType) => {
      return (
        <FilterForm
          isQueryBasedFilter={isQueryBasedFilter}
          filterConfig={filterConfig}
          filterArrayData={filterArrayData ?? []}
          filterData={filterData}
          setFilterArrayData={setFilterArrayData}
          startDate={startDate}
          endDate={endDate}
          filterIndex={index}
        />
      )
    },
    [filterArrayData]
  )

  const renderFilterDialogContent = () => {
    return (
      <>
        {filterArrayData?.map((filterData: FilterDataType, index: number) => {
          return (
            <>
              <div key={`${filterData?.columnName}`}>{renderFilterForm(index, filterData)}</div>
              <div
                className={`border-t-[0.5px] my-6 h-0 -ml-4 -mr-4 ${
                  index === filterArrayData.length - 1 ? 'hidden' : ''
                }`}
              >
                <span className='relative bg-[#f0f0f0] rounded-lg text-[#949494] text-xs -top-[13px] left-4 px-1 py-[0.5]'>
                  AND
                </span>
              </div>
            </>
          )
        })}
      </>
    )
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  return (
    <FloGenericDialog
      isOpen={isDialogOpen}
      width={600}
      handleClose={handleCloseDialog}
      handleJob={handleApplyFilters}
      jobButtonText={primaryButton}
      closeButtonText={secondaryButton}
      content={<>{renderFilterDialogContent()}</>}
      title={title}
      actionBtn={
        isQueryBasedFilter ? (
          <Button variant='outlined' className={`items-center max-w-fit gap-1`} onClick={handleAddFilter}>
            {<PlusCircle className='w-4 h-4 stroke-[2.5px] text-[#6e6e6e]' />}
            {'Add filter'}
          </Button>
        ) : (
          <></>
        )
      }
    />
  )
}

export default FilterFormDialog
